import { CancelSubscriptionDialog } from "@/components/cancel-subscription-dialog";
import { DeleteAccountDialog } from "@/components/delete-account-dialog";
import { useUser } from "@features/user/use-user";
import { formatNumber } from "@features/utils";
import {
  ArrowLeftEndOnRectangleIcon,
  ArrowLeftIcon,
  ShoppingCartIcon,
} from "@heroicons/react/16/solid";
import { ExternalLinkIcon } from "@radix-ui/react-icons";
import {
  Badge,
  Box,
  Button,
  Flex,
  Heading,
  IconButton,
  Text,
} from "@radix-ui/themes";
import { getPreviousPath, Page } from "@views/layout/layout";
import { SideBarContainer } from "@views/layout/sidebar";
import { RealResultsFromRealPeople } from "@views/welcome/parts/real-results-from-real-people";
import { formatDate } from "date-fns";
import { AnimatePresence, motion } from "framer-motion";
import posthog from "posthog-js";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { twMerge } from "tailwind-merge";
import { Env } from "../../../env";
import { ButtonLink } from "../home";

// Using Stripe's customer portal URL for existing subscribers
const billingUrl = "https://billing.stripe.com/p/login/dR66pI0bH7jRggM6oo";

const soon = ["spe_3", "pro_11", "pro_12"];

// Get plan color based on name
const getPlanColor = (plan: string) => {
  switch (plan) {
    case "single":
      return "gray";
    case "basic":
      return "blue";
    case "essential":
      return "green";
    case "advanced":
      return "purple";
    case "business":
      return "gold";
    default:
      return "gray";
  }
};

const PlanCard = ({
  plan,
  interval,
  isCurrentPlan,
  onSelect,
  className,
}: {
  plan: string;
  interval: "month" | "year" | "single";
  isCurrentPlan?: boolean;
  onSelect: (plan: string, interval: "month" | "year" | "single") => void;
  className?: string;
}) => {
  const { t } = useTranslation();
  const planData = t(`pricing_section.cards.${plan}_pricing`, {
    returnObjects: true,
  }) as any;

  useEffect(() => {
    posthog.capture("reached_billing", {});
  }, []);

  const price =
    interval === "year" ? planData.price_year : planData.price_month;

  const features = Object.entries(planData.features || {})
    .filter(([key]) => key !== "cancel")
    .map(([key, value]) => ({ key, value })) as {
    key: string;
    value: string;
  }[];

  const isRecommended = plan === "essential";
  const isLimitedOffer = plan === "basic";

  return (
    <div
      onClick={() => onSelect(plan, interval)}
      className={twMerge(
        `relative w-full max-w-xs transition-all duration-300 cursor-pointer hover:shadow-md rounded-lg`,
        isRecommended
          ? "border-2 shadow-lg"
          : "border-0 bg-slate-50 dark:bg-slate-900",
        className
      )}
      style={
        isRecommended
          ? {
              borderWidth: "2px",
              borderColor: getPlanColor(plan),
              background: `linear-gradient(135deg, ${getPlanColor(
                plan
              )}15 0%, ${getPlanColor(plan)}05 100%)`,
            }
          : {}
      }
    >
      {isRecommended && (
        <div
          className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full text-xs font-bold text-white"
          style={{ backgroundColor: getPlanColor(plan) }}
        >
          {t("pricing_section.popular")}
        </div>
      )}
      {isLimitedOffer && (
        <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full text-xs font-bold bg-red-500 text-white">
          {t("pricing_section.limited_offer")}
        </div>
      )}
      <Box className="p-4 flex flex-col h-full text-left">
        <Heading size="4" className="mb-2">
          {planData.plan}
        </Heading>
        <Text size="5" weight="bold" className="mb-4">
          {price}
          <Text size="2" weight="regular">
            {interval === "year"
              ? t("pricing_section.payment_per_year")
              : t("pricing_section.payment_per_month")}
          </Text>
        </Text>
        <ul className="list-disc pl-5 space-y-1 flex-grow">
          {features
            .filter(
              ({ key }) => !["base_7"].includes(key) // Temporarily hide the filigrane stuff
            )
            .map(({ key, value }, idx) => (
              <li key={idx}>
                <div
                  className={twMerge(
                    "space-x-2",
                    ["base_6", "base_7"].includes(key) ? "text-yellow-400" : ""
                  )}
                >
                  <Text size="2">{value}</Text>
                  {soon.includes(key) && (
                    <span className="text-xs opacity-50">
                      ({t("pricing_section.coming_soon")})
                    </span>
                  )}
                </div>
              </li>
            ))}
        </ul>
        <Button
          className={twMerge("mt-4", isRecommended && "hue-animation")}
          size={"3"}
          variant={isCurrentPlan ? "outline" : "solid"}
          disabled={isCurrentPlan}
          onClick={isCurrentPlan ? undefined : () => onSelect(plan, interval)}
        >
          {isCurrentPlan
            ? t("billing.current_plan_button")
            : t("general.continue")}
        </Button>
      </Box>
    </div>
  );
};

export const Billing = () => {
  const { user } = useUser();
  const plan = user?.data?.plan || "free";
  const { t } = useTranslation();
  const navigate = useNavigate();

  // For now we'll keep it like that until we have more things in the sidebar
  if (true) {
    return (
      <div className="flex w-full md:min-h-[90vh] items-center justify-center max-w-6xl mx-auto space-x-8">
        <div className="grow p-4 max-w-3xl">
          <AnimatePresence mode="wait">
            <motion.div
              key="billing-page"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.3, ease: "easeInOut" }}
              className=""
            >
              <div className="mb-4 md:block hidden">
                <Button
                  variant="ghost"
                  onClick={() => navigate(`/`)}
                  className="flex items-center gap-2"
                >
                  <ArrowLeftIcon className="h-4 w-4" />
                  {t("welcome.back")}
                </Button>
              </div>
              <BillingContent />
            </motion.div>
          </AnimatePresence>
        </div>
        {plan === "free" && <RealResultsFromRealPeople />}
      </div>
    );
  }

  return (
    <Page verticalCenter={false}>
      <AnimatePresence mode="wait">
        <motion.div
          key="billing-page"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -20 }}
          transition={{ duration: 0.3, ease: "easeInOut" }}
          className="w-full flex"
        >
          <div className="">
            <AccountSideBar />
          </div>
          <div className="grow space-y-8">
            <BillingContent />
          </div>
        </motion.div>
      </AnimatePresence>
    </Page>
  );
};

export const BillingContent = () => {
  const { user, cancelSubscription, deleteAccount, cancelAccountDeletion } =
    useUser();
  const plan = user?.data?.plan || "free";
  const [interval, setInterval] = useState(
    plan === "free" ? "month" : "single"
  );
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const { t } = useTranslation();

  const availablePlans = ["basic", "essential", "advanced", "business"];

  // Check if account deletion is scheduled
  const isDeletionScheduled = !!(
    user?.data?.to_delete && user.data.to_delete > 1000000
  );
  const deletionDate = isDeletionScheduled
    ? new Date(user.data!.to_delete + 48 * 60 * 60 * 1000)
    : undefined;

  const handleSelectPlan = (selectedPlan: string, selectedInterval: string) => {
    const checkoutUrl = `${Env.server}/api/billing/checkout?plan=${selectedPlan}&interval=${selectedInterval}&id=${user?.data?.id}&lang=${user?.data?.lang}`;
    window.location.href = checkoutUrl;
  };

  const showRecap = plan !== "free" || (user.data?.credits_used || 0) > 150;

  const handleCancelSubscription = async () => {
    setIsCancelling(true);
    try {
      const success = await cancelSubscription();
      if (success) {
        toast.success(t("billing.cancel_dialog.success"));
        setShowCancelDialog(false);
      } else {
        toast.error(t("billing.cancel_dialog.error"));
      }
    } catch (error) {
      toast.error(t("billing.cancel_dialog.error"));
    } finally {
      setIsCancelling(false);
    }
  };

  const handleDeleteAccount = async () => {
    setIsDeleting(true);
    try {
      let success;
      if (isDeletionScheduled) {
        // Cancel deletion
        success = await cancelAccountDeletion();
        if (success) {
          setShowDeleteDialog(false);
        }
      } else {
        // Schedule deletion
        success = await deleteAccount();
        if (success) {
          setShowDeleteDialog(false);
        }
      }
    } catch (error) {
      console.error(error);
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <div className="">
      <Heading size="8" className="mb-4">
        {t("header.billing")}
      </Heading>

      {showRecap && (
        <>
          <Heading size="5">{t("billing.current_plan")}</Heading>
          <Flex direction="column" gap="3" className="mt-2">
            <Flex gap="3" align="center">
              <Badge
                size="3"
                className={"block w-max"}
                style={{
                  backgroundColor: getPlanColor(plan),
                  color: "white",
                }}
                variant="solid"
              >
                <Heading size="4" className="inline-block">
                  {t("billing.plan_name", {
                    plan: plan.charAt(0).toUpperCase() + plan.slice(1),
                  })}
                </Heading>
              </Badge>
              <Text size="2" color="gray" className="block">
                {user?.data?.plan_cancel_at && user.data.plan_cancel_at > 0 ? (
                  <span className="text-orange-600">
                    {t("billing.cancels_on", {
                      date: formatDate(
                        new Date(user.data.plan_cancel_at),
                        "yyyy-MM-dd"
                      ),
                    })}
                  </span>
                ) : (user?.data?.plan_renew_at || 0) > 0 ? (
                  t("billing.renews_on", {
                    date: formatDate(
                      new Date(user?.data?.plan_renew_at || 0),
                      "yyyy-MM-dd"
                    ),
                  })
                ) : (
                  ""
                )}
              </Text>
            </Flex>

            <Box className="bg-slate-50 dark:bg-slate-800 p-3 rounded-lg">
              <Text className="mb-2" weight="bold">
                {t("billing.your_credits")}
              </Text>
              <Box>
                <Text size="2" color="gray">
                  {t("billing.remaining")}
                </Text>
                <Text size="2" className="ml-2" weight="bold">
                  {formatNumber(user?.data?.credits || 0)}
                </Text>
              </Box>
              <Box>
                <Text size="2" color="gray">
                  {t("billing.next_renewal")}
                </Text>
                <Text size="2" className="ml-2" weight="bold">
                  {formatNumber(user?.data?.plan_credits || 0)}
                </Text>
              </Box>
              {!!user?.data?.additional_credits && (
                <Box>
                  <Text size="2" color="gray">
                    {t("billing.remaining_additional")}
                  </Text>
                  <Text size="2" className="ml-2" weight="bold">
                    {formatNumber(user?.data?.additional_credits || 0)}
                  </Text>
                </Box>
              )}

              <br />

              <Text weight="bold" className="mb-2">
                {t("billing.can_create")}
              </Text>
              <ul className="list-disc pl-5 w-max">
                <li>
                  <Text>
                    {t("billing.models_count", {
                      replace: {
                        count: formatNumber(
                          Math.floor(
                            ((user?.data?.credits || 0) +
                              (user?.data?.additional_credits || 0)) /
                              400
                          )
                        ),
                      },
                    })}
                  </Text>
                </li>
                <li>
                  <Text>
                    {t("billing.images_count", {
                      replace: {
                        count: formatNumber(
                          Math.floor(
                            ((user?.data?.credits || 0) +
                              (user?.data?.additional_credits || 0)) /
                              2
                          )
                        ),
                      },
                    })}
                  </Text>
                </li>
                <li>
                  <Text>
                    {t("billing.videos_count", {
                      replace: {
                        count: formatNumber(
                          Math.floor(
                            ((user?.data?.credits || 0) +
                              (user?.data?.additional_credits || 0)) /
                              30
                          )
                        ),
                      },
                    })}
                  </Text>
                </li>
              </ul>
            </Box>

            {plan !== "free" && (
              <Box className="mb-2 flex flex-col gap-2 md:flex-row items-start">
                <a href={billingUrl} target="_blank" rel="noopener noreferrer">
                  <Button>
                    {t("billing.manage_subscription")} <ExternalLinkIcon />
                  </Button>
                </a>

                {user?.data?.plan_cancel_at && user.data.plan_cancel_at > 0 ? (
                  <Text size="2" color="orange" weight="medium">
                    {t("billing.subscription_cancelled")}
                  </Text>
                ) : (
                  <Button
                    color="red"
                    variant="soft"
                    onClick={() => setShowCancelDialog(true)}
                  >
                    {t("billing.cancel_subscription")}
                  </Button>
                )}
              </Box>
            )}
          </Flex>
        </>
      )}

      {showRecap && (
        <>
          <br />
          <br />

          <Heading size="5">{t("billing.compare_plans")}</Heading>
        </>
      )}

      <div className="mb-8 mt-4 mx-auto flex justify-center md:justify-start">
        {/* Billing interval toggle */}
        <div className="inline-flex items-center bg-slate-100 dark:bg-slate-800 p-1 rounded-full">
          <button
            className={`px-4 py-2 rounded-full ${
              interval === "month"
                ? "bg-white dark:bg-blue-500 shadow-sm"
                : "text-slate-500 dark:text-slate-200"
            }`}
            onClick={() => setInterval(() => "month")}
          >
            {t("pricing_section.monthly")}
          </button>
          <button
            className={`px-4 py-2 rounded-full relative relative ${
              interval === "year"
                ? "bg-white dark:bg-blue-500 shadow-sm"
                : "text-slate-500 dark:text-slate-200"
            }`}
            onClick={() => setInterval(() => "year")}
          >
            {t("pricing_section.yearly")}
            {false && (
              <Badge
                size="1"
                color="red"
                variant="solid"
                className="absolute top-9 left-1/2 -translate-x-1/2 w-max max-w-24 whitespace-normal"
              >
                {t("pricing_section.yearly_discount")}
              </Badge>
            )}
          </button>
          <button
            className={`px-4 py-2 rounded-full ${
              interval === "single"
                ? "bg-white dark:bg-blue-500 shadow-sm"
                : "text-slate-500 dark:text-slate-200"
            }`}
            onClick={() => setInterval(() => "single")}
          >
            {t("pricing_section.single")}
          </button>
        </div>
      </div>

      {(interval === "year" || interval === "month") && (
        <AnimatePresence mode="wait">
          <motion.div
            key={interval}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3, ease: "easeInOut" }}
          >
            <Flex
              wrap="wrap"
              gap="4"
              className="justify-center md:justify-start"
            >
              {availablePlans.map((planName) => (
                <PlanCard
                  key={planName}
                  plan={planName}
                  interval={interval as "month" | "year"}
                  isCurrentPlan={plan === planName}
                  onSelect={handleSelectPlan}
                />
              ))}
            </Flex>
          </motion.div>
        </AnimatePresence>
      )}

      {interval === "single" && (
        <AnimatePresence mode="wait">
          <motion.div
            key={interval}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3, ease: "easeInOut" }}
          >
            <div
              onClick={() => {
                handleSelectPlan("single_1000", "1000");
              }}
              className={twMerge(
                `mt-4 w-full transition-all duration-300 cursor-pointer hover:shadow-md rounded-lg border-2 border-gray`
              )}
            >
              <Box className="p-4 flex flex-col h-full">
                <Heading size="4" className="mb-2">
                  {t("pricing_section.cards.refill.plan")}
                </Heading>
                <Text size="5" weight="bold" className="mb-4">
                  {t("pricing_section.cards.refill.price")}
                </Text>
                <Text className="mb-4">
                  {plan === "free"
                    ? t("pricing_section.cards.refill.description_free")
                    : t("pricing_section.cards.refill.description")}
                </Text>
                <ul className="list-disc pl-5 space-y-1 flex-grow">
                  {(
                    Object.entries(
                      (
                        t("pricing_section.cards.refill", {
                          returnObjects: true,
                        }) as any
                      ).features || {}
                    )
                      .filter(([key]) => key !== "cancel")
                      .map(([key, value]) => ({ key, value })) as {
                      key: string;
                      value: string;
                    }[]
                  ).map(({ key, value }, idx) => (
                    <li key={idx}>
                      <Flex align="center" gap="2">
                        <Text size="2">{value}</Text>
                        {soon.includes(key) && (
                          <span className="text-xs opacity-50">
                            ({t("pricing_section.coming_soon")})
                          </span>
                        )}
                      </Flex>
                    </li>
                  ))}
                </ul>
                <Button className="mt-4" size={"2"}>
                  {t("pricing_section.cards.refill.cta_name")}
                </Button>
              </Box>
            </div>

            <div
              onClick={() => {
                handleSelectPlan("single_3000", "3000");
              }}
              className={twMerge(
                `mt-4 w-full transition-all duration-300 cursor-pointer hover:shadow-md rounded-lg border-2 border-gray`
              )}
            >
              <Box className="p-4 flex flex-col h-full">
                <Heading size="4" className="mb-2">
                  {t("pricing_section.cards.refill_3000.plan")}
                </Heading>
                <Text size="5" weight="bold" className="mb-4">
                  {t("pricing_section.cards.refill_3000.price")}
                </Text>
                <Text className="mb-4">
                  {plan === "free"
                    ? t("pricing_section.cards.refill.description_free")
                    : t("pricing_section.cards.refill.description")}
                </Text>
                <ul className="list-disc pl-5 space-y-1 flex-grow">
                  {(
                    Object.entries(
                      (
                        t("pricing_section.cards.refill_3000", {
                          returnObjects: true,
                        }) as any
                      ).features || {}
                    )
                      .filter(([key]) => key !== "cancel")
                      .map(([key, value]) => ({ key, value })) as {
                      key: string;
                      value: string;
                    }[]
                  ).map(({ key, value }, idx) => (
                    <li key={idx}>
                      <Flex align="center" gap="2">
                        <Text size="2">{value}</Text>
                        {soon.includes(key) && (
                          <span className="text-xs opacity-50">
                            ({t("pricing_section.coming_soon")})
                          </span>
                        )}
                      </Flex>
                    </li>
                  ))}
                </ul>
                <Button className="mt-4" size={"2"}>
                  {t("pricing_section.cards.refill_3000.cta_name")}
                </Button>
              </Box>
            </div>
          </motion.div>
        </AnimatePresence>
      )}

      <Text size="1" className="mt-6 text-center">
        {t("pricing_section.payments_secured")}
      </Text>

      <Box className="mt-12 p-4 border-2 border-red-200 dark:border-red-900 rounded-lg">
        <Heading size="4" className="mb-2 text-red-600 dark:text-red-400">
          {t("delete_account.danger_zone", "Danger Zone")}
        </Heading>
        <Text size="2" color="gray" className="mb-4 block">
          {t(
            "delete_account.danger_description",
            "Once you delete your account, there is no going back. Please be certain."
          )}
        </Text>
        <Button
          color={isDeletionScheduled ? "orange" : "red"}
          variant="outline"
          onClick={() => setShowDeleteDialog(true)}
        >
          {isDeletionScheduled
            ? t("delete_account.cancel_scheduled", "Cancel Scheduled Deletion")
            : t("delete_account.button", "Delete My Account")}
        </Button>
      </Box>

      <CancelSubscriptionDialog
        isOpen={showCancelDialog}
        onClose={() => setShowCancelDialog(false)}
        onConfirm={handleCancelSubscription}
        isLoading={isCancelling}
      />

      <DeleteAccountDialog
        isOpen={showDeleteDialog}
        onClose={() => setShowDeleteDialog(false)}
        onConfirm={handleDeleteAccount}
        isLoading={isDeleting}
        isScheduled={isDeletionScheduled}
        scheduledDate={deletionDate}
      />
    </div>
  );
};

export const BillingApple = () => {
  return <div></div>;
};

export const AccountSideBar = () => {
  const { t } = useTranslation();
  return (
    <SideBarContainer>
      <div className="pt-4 space-x-2 flex items-center">
        <Link to={getPreviousPath()}>
          <IconButton size="1" variant="soft" color="gray">
            <ArrowLeftIcon className="h-4 w-4" />
          </IconButton>
        </Link>
        <Heading size="2" className="inline-block">
          {t("billing.account")}
        </Heading>
      </div>
      <div className="h-4" />
      <Heading size="2" className="mb-2">
        {t("billing.account")}
      </Heading>
      <ButtonLink href="/billing" icon={(p) => <ShoppingCartIcon {...p} />}>
        {t("header.billing")}
      </ButtonLink>
      <div className="h-4" />
      <ButtonLink
        href="/logout"
        icon={(p) => <ArrowLeftEndOnRectangleIcon {...p} />}
      >
        {t("header.logout")}
      </ButtonLink>
    </SideBarContainer>
  );
};
